import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials, SignupData, RegisterCredentials, UpdateProfileData, WaitlistSignupData } from '../types/auth';
import { authService } from '../services/auth';
import { normalizeUserData } from '../utils/authHelpers';
import { mcpHostService } from '../services/mcp-host';

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  tenantId: string | null;
}

interface AuthStore extends AuthState {

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  waitlistSignup: (data: WaitlistSignupData) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  refreshAuthToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  setTokens: (token: string, refreshToken?: string) => void;
  getTenantId: () => string | null;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      tenantId: null,

      // Login action - Updated for AA backend integration with auto-connect
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(credentials);
          const tenantId = authService.getTenantId();
          
          set({
            user: normalizeUserData(response.user),
            isAuthenticated: true,
            isLoading: false,
            error: null,
            tenantId,
          });

          // 🚀 AUTO-CONNECT MCP SERVERS ON LOGIN SUCCESS
          // This is the key enhancement that removes the manual connection step
          if (tenantId) {
            try {
              console.log('🔗 Auto-connecting MCP servers for user:', tenantId);
              const connectResult = await mcpHostService.connectUserMCPServers(tenantId);
              
              if (connectResult.success) {
                console.log('✅ MCP servers auto-connected successfully:', connectResult.message);
              } else {
                console.warn('⚠️ MCP auto-connect failed (non-blocking):', connectResult.message);
              }
            } catch (mcpError) {
              // Don't fail login if MCP auto-connect fails
              console.warn('⚠️ MCP auto-connect error (non-blocking):', mcpError);
            }
          }
        } catch (error) {
          console.log('🏪 AuthStore login error:', error);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Login failed',
            tenantId: null,
          });
          throw error;
        }
      },

      // Signup action - Email verification required before authentication
      signup: async (data: SignupData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.signup(data);
          // User created but NOT authenticated until email verified
          set({
            user: null, // Don't set user until verified
            isAuthenticated: false, // Not authenticated until email verified
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Signup failed',
          });
          throw error;
        }
      },

      // Waitlist signup action - for users joining the waitlist
      waitlistSignup: async (data: WaitlistSignupData) => {
        set({ isLoading: true, error: null });
        try {
          await authService.waitlistSignup(data);
          // Don't authenticate - user is on waitlist
          set({
            isLoading: false,
            isAuthenticated: false,
            user: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Waitlist signup failed',
          });
          throw error;
        }
      },

      // Logout action - Updated for AA backend integration
      logout: async () => {
        set({ isLoading: true, error: null });
        try {
          await authService.logout();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            tenantId: null,
          });
        } catch (error) {
          // Even if API call fails, clear local state
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            tenantId: null,
          });
        }
      },

      // Check authentication status - Updated for AA backend integration
      checkAuth: async () => {
        const token = localStorage.getItem('auth_token');
        const refreshToken = localStorage.getItem('refresh_token');
        const tenantId = localStorage.getItem('tenant_id');
        
        // If no token, immediately set unauthenticated state
        if (!token) {
          set({ 
            isAuthenticated: false, 
            user: null, 
            tenantId: null, 
            isLoading: false, 
            error: null 
          });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const response = await authService.getCurrentUser();
          set({
            user: normalizeUserData(response.user),
            isAuthenticated: true,
            isLoading: false,
            error: null,
            tenantId: tenantId,
          });
        } catch (error) {
          // Only attempt refresh if we have a refresh token and the error is 401
          if (refreshToken && error instanceof Error && 
              (error.message.includes('401') || error.message.includes('Unauthorized'))) {
            try {
              await get().refreshAuthToken();
              // If refresh succeeds, try getting current user again
              const response = await authService.getCurrentUser();
              set({
                user: normalizeUserData(response.user),
                isAuthenticated: true,
                isLoading: false,
                error: null,
                tenantId: authService.getTenantId(),
              });
              return;
            } catch (refreshError) {
              // Refresh failed, fall through to clear auth state
              console.debug('Token refresh failed during auth check:', refreshError);
            }
          }
          
          // Clear auth state silently (401 errors are expected when not logged in)
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null, // Don't set error for expected 401s
            tenantId: null,
          });
          
          // Clear tokens
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('tenant_id');
        }
      },

      // Register action (alias for signup)
      register: async (credentials: RegisterCredentials) => {
        const signupData: SignupData = {
          email: credentials.email,
          fullName: `${credentials.firstName} ${credentials.lastName}`,
          acceptTerms: true, // Assume terms are accepted for register
        };
        return get().signup(signupData);
      },

      // Update profile
      updateProfile: async (data: UpdateProfileData) => {
        set({ isLoading: true, error: null });
        try {
          // Implement profile update logic here
          const currentUser = get().user;
          if (currentUser) {
            const updatedUser = normalizeUserData({ ...currentUser, ...data });
            set({ user: updatedUser, isLoading: false, error: null });
          }
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Profile update failed',
          });
          throw error;
        }
      },

      // Refresh auth token - Updated for AA backend integration
      refreshAuthToken: async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        
        // Don't attempt refresh if no refresh token
        if (!refreshToken) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            tenantId: null,
          });
          throw new Error('No refresh token available');
        }

        set({ isLoading: true, error: null });
        try {
          const response = await authService.refreshToken();
          const tenantId = authService.getTenantId();
          
          set({
            user: normalizeUserData(response.user),
            isAuthenticated: true,
            isLoading: false,
            error: null,
            tenantId,
          });
        } catch (error) {
          // Token refresh failed, clear auth state silently
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null, // Don't set error for failed refresh as it's often expected
            tenantId: null,
          });
          
          // Clear tokens
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('tenant_id');
          
          console.debug('Token refresh failed:', error);
          throw error;
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
      
      // Set tokens (utility method)
      setTokens: (token: string, refreshToken?: string) => {
        localStorage.setItem('auth_token', token);
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        }
      },
      
      // Get tenant ID
      getTenantId: () => {
        return get().tenantId || localStorage.getItem('tenant_id');
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated,
        tenantId: state.tenantId 
      }),
    }
  )
);